import { Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../../../api/hooks/useIsIntegrated";
import { controlKeys, labelKeys } from "../../../../../../api/queryKeys";
import CopyButton from "../../../../../../components/buttons/CopyButton";
import DeleteAtlassianApiToken from "../../../../../../components/buttons/DeleteAtlassianApiToken";
import DisablePasswordRevealInOktaButton from "../../../../../../components/buttons/DisablePasswordRevealInOktaButton";
import ExcludeOrIncludeButton from "../../../../../../components/buttons/ExcludeOrIncludeButton";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import RemovePersonalEmailPermissionsButton from "../../../../../../components/buttons/RemovePersonalEmailPermissionsButton";
import RestrictGoogleFileAccessButton from "../../../../../../components/buttons/RestrictGoogleFileAccessButton";
import RevokeAtlassianAccessUserButton from "../../../../../../components/buttons/RevokeAtlassianAccessUserButton";
import RevokeAtlassianApiKey from "../../../../../../components/buttons/RevokeAtlassianApiKey";
import RevokeGoogleTokensButton from "../../../../../../components/buttons/RevokeGoogleTokensButton";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import { Toast } from "../../../../../../components/Toast";
import {
  ATLASSIAN_CONTROL_WITH_DELETE_API_KEY_BUTTON,
  ATLASSIAN_CONTROL_WITH_DELETE_API_TOKEN_BUTTON,
  ATLASSIAN_CONTROL_WITH_REVOKE_USER_ACCESS,
  ComplexEntityTypes,
  CONTROLS_WITH_REVOKE_TOKEN_BUTTON,
  DISABLE_PASSWORD_REVEAL_IN_OKTA_CONTROL,
  ENTITY_TYPE_WITH_REVOKE_TOKEN_BUTTON,
  GoogleFileHeader,
  REMOVE_PERSONAL_EMAIL_PERMISSIONS_CONTROL,
  RESTRICT_GOOGLE_FILE_ACCESS_CONTROL,
} from "../../../../../../utils/constants";
import {
  getEntityTypeDisplayName,
  isComplexEntityType,
} from "../../../../../../utils/utils";
import EditLabelsButton from "../../../../../stack/environment/components/EditLabelsButton";
import IssueCreationModal from "../../../../../stack/integrations/Jira/IssueCreationModal";
import JiraCreateMultipleIssuesButton from "../../../../../stack/integrations/Jira/JiraCreateMultipleIssuesButton";
import {
  affectedEntitiesToCsv,
  csvExportFile,
} from "../../EntitiesSection/ExportTable";
import { extractTypeFromEntityId } from "../../EntitiesSection/utils";
import { SnoozeType } from "../../SnoozeButton/constants";
import SnoozeButton from "../../SnoozeButton/SnoozeButton";
import { MisconfigStatusTab } from "../tables/MisconfigStatusTabs";
import FilterMisconfigurationsButton from "./FilterMisconfigurationsButton";
export default function ControlEntitiesActionButtons({
  fullControl,
  entitiesByTypeAndStatus,
  checkedEntityIDs,
  selectedEntityTypeTab,
  selectedStatusTab,
  actionsOnMisconfigurations = false,
  parentMisconfiguration = null,
  appliedFilters,
  setAppliedFilters,
}) {
  const [isJiraCreationModalOpen, setIsJiraCreationModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const {
    trackEvent,
    exportControlMisconfigurations,
    updateControlExclusions,
  } = useAPI();

  const { isIntegrated } = useIsIntegrated("jira");
  // By definition, the Jira button is currently available only for misconfigurations in grouped controls
  const includeJira = isIntegrated && actionsOnMisconfigurations;

  const checkedMisconfigurationObjects =
    (actionsOnMisconfigurations &&
      fullControl?.misconfigurations?.filter((fullMisconfig) =>
        checkedEntityIDs.includes(fullMisconfig.entity_id)
      )) ||
    [];

  const checkedMisconfigurationIDs =
    (actionsOnMisconfigurations &&
      checkedMisconfigurationObjects.map(
        (fullMisconfig) => fullMisconfig.misconfiguration_id
      )) ||
    [];

  const checkedAffectedEntities =
    (!actionsOnMisconfigurations &&
      fullControl?.misconfigurations?.flatMap((fullMisconfig) =>
        fullMisconfig.affected_entities?.filter((affectedEntity) =>
          checkedEntityIDs.includes(affectedEntity.entity_id)
        )
      )) ||
    [];

  const checkedAffectedEntityMisconfigIds =
    (!actionsOnMisconfigurations &&
      checkedAffectedEntities.map(
        (affectedEntity) => affectedEntity.misconfiguration_id
      )) ||
    [];

  // For token revoke
  const createTokenToUsersMapping = () => {
    if (
      selectedEntityTypeTab === "google_token" &&
      actionsOnMisconfigurations
    ) {
      // For tokens, create a mapping where each token maps to its affected users
      return checkedEntityIDs.reduce((mapping, tokenEntityId) => {
        const fullMisconfig = checkedMisconfigurationObjects.find(
          (fullMisconfig) => fullMisconfig.entity_id === tokenEntityId
        );
        if (fullMisconfig) {
          mapping[tokenEntityId] = fullMisconfig.affected_entity_ids.filter(
            (entityId) =>
              extractTypeFromEntityId(entityId) === ComplexEntityTypes.USER
          );
        }
        return mapping;
      }, {});
    } else if (parentMisconfiguration) {
      // We are in the affected entities table
      // Create mapping from parent misconfiguration to checked entity IDs
      return { [parentMisconfiguration.entity_id]: checkedEntityIDs };
    }
    return null;
  };

  // For Google file restriction
  const createFileRestrictionList = () => {
    if (!actionsOnMisconfigurations) {
      return null;
    }

    return checkedEntityIDs.map((entityId) => {
      const misconfiguration = checkedMisconfigurationObjects.find(
        (misc) => misc.entity_id === entityId
      );

      if (misconfiguration && misconfiguration.extra_info) {
        const driveId = (() => {
          const entry = misconfiguration?.extra_info?.find((info) =>
            info?.startsWith(GoogleFileHeader.DRIVE_ID)
          );
          if (!entry) return undefined;
          const firstDash = entry.indexOf("-");
          return firstDash === -1 ? undefined : entry.slice(firstDash + 1);
        })();

        // Get owner email from extra_info (index 1 is owner email in GoogleFileHeader.OWNER)
        const ownerEmail = misconfiguration?.extra_info?.[1] || null;

        return {
          file_id: entityId,
          drive_id: driveId || null,
          owner_email: ownerEmail || null,
        };
      } else {
        return null;
      }
    });
  };

  const updateExclusion = async (reason = null) => {
    try {
      const is_excluding = selectedStatusTab === MisconfigStatusTab.INCLUDED;

      let newStateDescription;
      if (is_excluding) newStateDescription = "excluded";
      else newStateDescription = "included";

      trackEvent(
        `User is ${newStateDescription.slice(0, -2) + "ing"} ${
          checkedEntityIDs.length
        } entities ${is_excluding ? "from" : "to"} control #${
          fullControl?.control_id
        }`
      );

      const response = await updateControlExclusions(
        fullControl.control_id,
        checkedEntityIDs,
        is_excluding,
        reason
      );

      Toast.success(
        `The selected entities were successfully ${newStateDescription}.`
      );
      return response.data;
    } catch (err) {
      Toast.error("Error updating entity. Please try again.");
      return [];
    }
  };

  const exclusionMutation = useMutation({
    mutationFn: updateExclusion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: controlKeys.all });
    },
  });

  const exportMisconfigurationsToCSV = async () => {
    trackEvent(
      `User exported control ${fullControl?.control_id} misconfigurations table to CSV`
    );

    try {
      let csvData = await exportControlMisconfigurations(
        fullControl?.control_id,
        checkedMisconfigurationIDs
      );
      csvExportFile(
        csvData?.data,
        `control_#${fullControl?.control_id}_misconfigurations`
      );
    } catch {
      Toast.error("Error exporting to file.");
    }
  };

  const exportAffectedEntitiesToCSV = (type) => {
    trackEvent(
      `User exported control #${fullControl.control_id} affected entities to CSV`
    ).then();

    const labelsData = queryClient.getQueryData(labelKeys.all);
    const allLabels =
      labelsData?.data?.filter((label) => !label.is_deleted) || [];
    const labelIdToNameMap = allLabels.reduce((acc, label) => {
      acc[label.label_id] = label.name;
      return acc;
    }, {});

    affectedEntitiesToCsv(
      fullControl.control_id,
      entitiesByTypeAndStatus[selectedEntityTypeTab][selectedStatusTab],
      checkedEntityIDs,
      parentMisconfiguration,
      labelIdToNameMap
    );
  };

  const handleCopyChecked = () => {
    trackEvent("User copied misconfigured entities to clipboard");
    let entityNamesToCopy = [];
    entityNamesToCopy = entitiesByTypeAndStatus[selectedEntityTypeTab][
      selectedStatusTab
    ]
      .filter((entity) => {
        const isChecked = checkedEntityIDs.includes(entity?.entity_id);
        return isChecked;
      })
      .map((entity) => {
        return entity?.display_name;
      });
    navigator.clipboard.writeText(entityNamesToCopy.join(", "));
    Toast.success(
      `Copied selected ${pluralize(
        selectedEntityTypeTab,
        checkedEntityIDs.length
      )} to clipboard!`
    );
  };

  const handleCloseJiraCreationModal = () => {
    setIsJiraCreationModalOpen(false);
    trackEvent("User closed Jira creation modal");
  };

  const tokenToUsersMapping = createTokenToUsersMapping();
  const showRevokeTokensButton =
    CONTROLS_WITH_REVOKE_TOKEN_BUTTON.includes(fullControl.control_id) &&
    ENTITY_TYPE_WITH_REVOKE_TOKEN_BUTTON.includes(selectedEntityTypeTab) &&
    tokenToUsersMapping;

  // Check if all the checked entities are atlassian users for the revoke atlassian access button
  const entityObjectsIsOnlyAtlassianUser = () => {
    const entityObjects =
      entitiesByTypeAndStatus[selectedEntityTypeTab][selectedStatusTab];

    const entityObjectsChecked = entityObjects.filter((entity) => {
      return checkedEntityIDs?.includes(entity.entity_id);
    });

    // Return false if no entities are checked
    if (entityObjectsChecked.length === 0) {
      return false;
    }

    const isAtlassianUser = entityObjectsChecked.every((entity) =>
      entity.sources?.includes("atlassian")
    );

    return isAtlassianUser;
  };
  const entityObjectsChecked = entitiesByTypeAndStatus[selectedEntityTypeTab][
    selectedStatusTab
  ].filter((entity) => {
    return checkedEntityIDs?.includes(entity.entity_id);
  });

  const showRevokeAtlassianAccessButton =
    ATLASSIAN_CONTROL_WITH_REVOKE_USER_ACCESS.includes(
      fullControl.control_id
    ) && !parentMisconfiguration;

  const showRestrictGoogleFileAccessButton =
    RESTRICT_GOOGLE_FILE_ACCESS_CONTROL.includes(fullControl.control_id);

  const showRemovePersonalEmailPermissionsButton =
    REMOVE_PERSONAL_EMAIL_PERMISSIONS_CONTROL.includes(fullControl.control_id);

  const showDisablePasswordRevealInOktaButton =
    DISABLE_PASSWORD_REVEAL_IN_OKTA_CONTROL.includes(fullControl.control_id);

  const includedEntitiesCount = entitiesByTypeAndStatus
    ? Object.keys(entitiesByTypeAndStatus).reduce((sum, entityType) => {
        const list =
          entitiesByTypeAndStatus[entityType]?.[MisconfigStatusTab.INCLUDED] ||
          [];
        return sum + list.length;
      }, 0)
    : 0;
  const isExcludingAll = checkedEntityIDs.length === includedEntitiesCount;

  const showDeleteAtlassianApiTokenButton =
    ATLASSIAN_CONTROL_WITH_DELETE_API_TOKEN_BUTTON.includes(
      fullControl.control_id
    );

  const showDeleteAtlassianApiKeyButton =
    ATLASSIAN_CONTROL_WITH_DELETE_API_KEY_BUTTON.includes(
      fullControl.control_id
    );

  return (
    <Box display="flex" gap={1}>
      <Box>
        <FilterMisconfigurationsButton
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          disabled={!isComplexEntityType(selectedEntityTypeTab)}
        />
      </Box>
      {includeJira && (
        <JiraCreateMultipleIssuesButton
          fullControl={fullControl}
          selectedMisconfigurations={checkedMisconfigurationIDs}
          onClick={() => setIsJiraCreationModalOpen(true)}
          loading={false}
        />
      )}
      {showRevokeTokensButton && (
        <RevokeGoogleTokensButton
          controlId={fullControl.control_id}
          tokenToUsersMapping={tokenToUsersMapping}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      {showRevokeAtlassianAccessButton && (
        <RevokeAtlassianAccessUserButton
          controlId={fullControl.control_id}
          userId={parentMisconfiguration?.entity_id}
          resourceIds={checkedEntityIDs}
          disabled={
            checkedEntityIDs.length === 0 || !entityObjectsIsOnlyAtlassianUser
          }
        />
      )}
      {showDeleteAtlassianApiTokenButton && (
        <DeleteAtlassianApiToken
          controlId={fullControl.control_id}
          apiTokens={checkedEntityIDs}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      {showDeleteAtlassianApiKeyButton && (
        <RevokeAtlassianApiKey
          controlId={fullControl.control_id}
          apiKeys={checkedEntityIDs}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      {showRestrictGoogleFileAccessButton && (
        <RestrictGoogleFileAccessButton
          controlId={fullControl.control_id}
          filesToRestrict={createFileRestrictionList()}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      {showRemovePersonalEmailPermissionsButton && (
        <RemovePersonalEmailPermissionsButton
          controlId={fullControl.control_id}
          filesToRestrict={createFileRestrictionList()}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      {showDisablePasswordRevealInOktaButton && (
        <DisablePasswordRevealInOktaButton
          controlId={fullControl.control_id}
          appIds={checkedEntityIDs}
          disabled={checkedEntityIDs.length === 0}
        />
      )}
      <ExcludeOrIncludeButton
        is_excluded={selectedStatusTab === MisconfigStatusTab.EXCLUDED}
        handleExclusion={(reason) => exclusionMutation.mutateAsync(reason)}
        toolTipText={{
          exclude: "Exclude selected entities from risk",
          include: "Include selected entities in risk",
        }}
        disabled={
          checkedEntityIDs.length === 0 ||
          selectedStatusTab === MisconfigStatusTab.SNOOZED
        }
        disabledTooltipText={
          selectedStatusTab === MisconfigStatusTab.SNOOZED
            ? {
                exclude: "The entities are already snoozed",
                include: "The entities are already snoozed",
              }
            : {
                exclude: "Select entities to exclude from risk",
                include: "Select entities to include in risk",
              }
        }
        isExcludingAll={isExcludingAll}
        isExcludingMisconfiguration={!Boolean(parentMisconfiguration)}
      />
      <SnoozeButton
        key={selectedStatusTab}
        initialIsSnooze={selectedStatusTab === MisconfigStatusTab.SNOOZED}
        misconfigIds={
          actionsOnMisconfigurations
            ? checkedMisconfigurationIDs
            : checkedAffectedEntityMisconfigIds
        }
        snoozeType={
          actionsOnMisconfigurations
            ? SnoozeType.MISCONFIGURATION
            : SnoozeType.AFFECTED_ENTITY
        }
        fromDrawer={true}
        hideTitle={true}
        size="medium"
        disabled={selectedStatusTab === MisconfigStatusTab.EXCLUDED}
        tooltipTitle={
          selectedStatusTab === MisconfigStatusTab.EXCLUDED
            ? "The misconfiguration is already excluded"
            : null
        }
      />
      <CopyButton
        handleCopy={() => {
          handleCopyChecked();
        }}
        itemsCount={checkedEntityIDs.length || 0}
        itemName={getEntityTypeDisplayName(selectedEntityTypeTab) || "entity"}
      />
      <CustomToolTip
        title={
          !isComplexEntityType(selectedEntityTypeTab)
            ? "Labeling is available in the affected entities view."
            : ""
        }
        placement="top"
      >
        <Box>
          <EditLabelsButton
            checkedEntities={entityObjectsChecked}
            showLabel={false}
            disabled={!isComplexEntityType(selectedEntityTypeTab)}
          />
        </Box>
      </CustomToolTip>
      <ExportButton
        handleExport={() =>
          actionsOnMisconfigurations
            ? exportMisconfigurationsToCSV()
            : exportAffectedEntitiesToCSV()
        }
      />
      {isJiraCreationModalOpen && (
        <IssueCreationModal
          open={isJiraCreationModalOpen}
          onClose={handleCloseJiraCreationModal}
          fullControl={fullControl}
          isPerMisconfiguration={true}
          selectedMisconfigurations={checkedMisconfigurationObjects}
        />
      )}
    </Box>
  );
}
